import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { downloadFile } from '../../../functions/download-file';
import {
  SystemFilter,
  UserFilter,
} from '../../../models/api/reports/all-filters-response';
import { CompletedReport } from '../../../models/api/reports/all-reports-response';
import { FilterDetailResponse } from '../../../models/api/reports/filter-detail-response';
import { DisplayService } from '../../../services/reports/display.service';
import { ReportsService } from '../../../services/reports/reports.service';
import { BaseComponent } from '../../base/base.component';
import { BackButtonComponent } from '../../core/buttons/back-button.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { CardComponent } from '../../core/panels/card.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { HomeCardComponent } from '../../home/home-card.component';
import { EmailRecipientsComponent } from '../core/email-recipients.component';
import { FilterViewComponent } from '../core/filter-view.component';
import { CompletedRequestViewComponent } from './completed-request-view.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-completed-report-page',
  imports: [
    RouterLink,
    BackButtonComponent,
    FilterViewComponent,
    EmailRecipientsComponent,
    CardComponent,
    ActionRowComponent,
    ButtonComponent,
    PageHeaderComponent,
    CompletedRequestViewComponent,
    LoadingPanelComponent,
    HomeCardComponent,
  ],
  template: `
    <app-page-header
      [label]="this.completedReportsData.outputName"
      labelHeader="Completed report"
      labelCase="none"
      headerSize="h1"
    >
      <ng-template #right>
        <app-action-row>
          @if (this.completedReportsData.fileFormat !== 'ERROR') {
            <app-button
              right1
              label="Download"
              icon="fa-light fa-arrow-down-to-line"
              title="Download report file"
              (click)="downloadReport(this.completedReportsData)"
            />
          }
          <app-button
            right2
            label="Delete"
            emphasis="Low"
            (click)="confirmDelete()"
          /> </app-action-row
      ></ng-template>
    </app-page-header>
    <div class="tw-flex">
      <app-back-button
        left1
        routerLink="/reports"
        [queryParams]="{ tab: 'completed' }"
      >
        Back
      </app-back-button>
    </div>
    <div class="tw-grow tw-justify-center tw-w-full">
      <div class="tw-min-w-0 tw-w-[100%] tw-pt-4 sm:tw-pt-8 tw-mb-4">
        <app-home-card type="Large">
          <div class="tw-min-w-0 tw-w-[100%] tw-p-4 sm:tw-p-8">
            <app-loading-panel
              [state]="loading ? 'Loading' : 'Loaded'"
              [error]="error"
              loadingStyle="Box"
              [loadingHeight]="500"
              loadingClasses="tw-pb-8"
            >
              <ng-template #loaded>
                <div class="tw-pb-8">
                  <app-completed-request-view
                    [completedReportsData]="completedReportsData"
                  />
                </div>
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 sm:tw-gap-8 tw-w-full tw-min-w-0">
                  @if (filterDetails) {
                    <app-card label="Filter" class="tw-basis-1/2 tw-min-w-0 tw-overflow-hidden">
                      <app-filter-view
                        [dataSource]="filterDetails.filterDetail"
                      />
                    </app-card>
                  }
                  @if (display.hasEmailAccess()) {
                    <app-card label="Email recipients" class="tw-basis-1/2 tw-min-w-0 tw-overflow-hidden">
                      <app-email-recipients
                        [archiveRequestId]="
                          completedReportsData.completedRequestId
                        "
                      />
                    </app-card>
                  }
                </div>
              </ng-template>
            </app-loading-panel>
          </div>
        </app-home-card>
      </div>
    </div>
  `,
  styles: ``,
})
export class CompletedReportPageComponent
  extends BaseComponent
  implements OnInit
{
  private reportsService = inject(ReportsService);

  display = inject(DisplayService);

  completedReportsData: CompletedReport;

  reportDescription: string;

  filterDetails: FilterDetailResponse;

  filters: (SystemFilter | UserFilter)[] = [];

  loading = false;
  error: any = null;

  @HandleLoadingState()
  async ngOnInit() {
    const report = this.reportsService.currentDetailReport() as CompletedReport;

    if (!report) {
      this.router.navigate(['/reports'], { replaceUrl: true });
      return;
    }

    this.completedReportsData = report;

    await this.loadFilterDetails();
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async loadFilterDetails() {
    const filterId = this.completedReportsData?.filterId;
    if (!filterId) {
      return;
    }

    const allFilters = await this.api.filters.getAllFilters(
      'JURIS',
      'CLAIM',
      this.completedReportsData?.contract,
    );

    this.filterDetails = await this.api.filters.getFilterDetail(
      filterId,
      this.completedReportsData?.contract,
    );
    this.filters = [...allFilters.standardFilters, ...allFilters.userFilters];
    const matchedFilter = this.filters.find(
      (f) => f.filterId === this.filterDetails.filterDetail.filterId,
    );
    if (matchedFilter) {
      this.filterDetails.filterDetail.filterName = matchedFilter.filterName;
    }
  }

  confirmDelete() {
    const dateCompleted = new Date(this.completedReportsData.dateCompleted)
      .toLocaleString()
      .replace(',', '');
    this.confirm.show({
      label: 'Delete report',
      message: `Are you sure you want to delete the completed report ${
        this.completedReportsData.reportName + ` (${dateCompleted})`
      }?`,
      width: '35rem',
      data: this.completedReportsData,
      confirm: (data) => this.confirmed(data),
    });
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async confirmed(report: CompletedReport) {
    await this.reportsService.deleteCompletedReport(
      report.completedRequestId,
      report.completedRequestOutputId,
    );
    this.router.navigate(['/reports'], {
      replaceUrl: true,
      queryParams: { tab: 'completed' },
    });
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async downloadReport(report: CompletedReport) {
    const blob = await this.reportsService.downloadCompletedReport(
      'JURIS',
      report.completedRequestOutputId,
    );
    const filename =
      report.filename || `report-${report.completedRequestOutputId}`;

    downloadFile(blob, filename, blob.type);
  }
}
